import { useState } from "react";
import {
    CheckCircle2,
    CreditCard,
    RefreshCw,
    Zap
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createCheckout, getPlans, getSubscription, verifyPayment, type Plan } from "@/api/portalApi";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Razorpay checkout.js types (loaded via script tag)
declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Razorpay: any;
    }
}

function ensureRazorpayScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (window.Razorpay) { resolve(); return; }
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("Failed to load Razorpay checkout script"));
        document.head.appendChild(s);
    });
}

type Cycle = "Monthly" | "Annually";

function parseFeatures(json: string): string[] {
    try { return JSON.parse(json) as string[]; } catch { return []; }
}

function fmtInr(n: number) {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
}

function PlanCard({
    plan,
    cycle,
    seatCount,
    onSelect,
    isCurrentPlan,
    loading,
}: {
    plan: Plan;
    cycle: Cycle;
    seatCount: number;
    onSelect: () => void;
    isCurrentPlan: boolean;
    loading: boolean;
}) {
    const price = cycle === "Annually" ? plan.priceAnnuallyInr : plan.priceMonthlyInr;
    const perSeat = seatCount > 1 ? price / seatCount : price;
    const features = parseFeatures(plan.featuresJson);
    const isHighlighted = plan.id === "business";

    return (
        <div
            className={cn(
                "relative flex flex-col rounded-xl border p-6 shadow-sm transition-shadow hover:shadow-md",
                isHighlighted
                    ? "border-primary bg-primary/5 shadow-primary/10"
                    : "border-border bg-card"
            )}
        >
            {isHighlighted && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground">
                    Most popular
                </span>
            )}
            <h3 className="text-lg font-semibold">{plan.name}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{plan.description}</p>

            <div className="mt-4">
                <span className="text-3xl font-bold">{fmtInr(price)}</span>
                <span className="ml-1 text-sm text-muted-foreground">
                    /{cycle === "Annually" ? "yr" : "mo"}
                    {seatCount > 1 && ` · ${fmtInr(perSeat)}/seat`}
                </span>
            </div>

            <ul className="mt-5 flex-1 space-y-2">
                <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
                    Up to {plan.maxUsers === 999 ? "unlimited" : plan.maxUsers} users
                </li>
                <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
                    {plan.maxStorageGb} GB storage / mailbox
                </li>
                {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm capitalize">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
                        {f}
                    </li>
                ))}
            </ul>

            <Button
                className="mt-6"
                variant={isHighlighted ? "default" : "outline"}
                onClick={onSelect}
                disabled={isCurrentPlan || loading}
            >
                {isCurrentPlan ? "Current plan" : loading ? "Processing…" : "Get started"}
            </Button>
        </div>
    );
}

export function BillingPage() {
    const { token } = useAuth();
    const qc = useQueryClient();
    const [cycle, setCycle] = useState<Cycle>("Monthly");
    const [seatCount, setSeatCount] = useState(5);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null); // planId being processed

    const plansQ = useQuery({ queryKey: ["plans"], queryFn: getPlans });
    const subQ   = useQuery({ queryKey: ["subscription", token], queryFn: () => getSubscription(token) });

    const activeSub = subQ.data && "id" in subQ.data ? subQ.data : null;

    const handleSelect = async (plan: Plan) => {
        setError(null);
        setSuccess(null);
        setCheckoutLoading(plan.id);
        try {
            await ensureRazorpayScript();
            const order = await createCheckout(token, plan.id, cycle, seatCount);

            await new Promise<void>((resolve, reject) => {
                const rzp = new window.Razorpay({
                    key: order.keyId,
                    order_id: order.orderId,
                    amount: order.amountInPaise,
                    currency: order.currency,
                    name: "Athavita Enterprise",
                    description: `${order.planName} — ${order.cycle} × ${order.seatCount} seats`,
                    prefill: {},
                    theme: { color: "#0078D4" },
                    handler: async (response: {
                        razorpay_order_id: string;
                        razorpay_payment_id: string;
                        razorpay_signature: string;
                    }) => {
                        try {
                            await verifyPayment(
                                token,
                                response.razorpay_order_id,
                                response.razorpay_payment_id,
                                response.razorpay_signature
                            );
                            await qc.invalidateQueries({ queryKey: ["subscription"] });
                            setSuccess(`${order.planName} activated successfully!`);
                            resolve();
                        } catch (e) {
                            reject(e);
                        }
                    },
                    modal: { ondismiss: () => resolve() }
                });
                rzp.on("payment.failed", (resp: { error: { description: string } }) => {
                    reject(new Error(resp.error.description));
                });
                rzp.open();
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : "Payment failed.");
        } finally {
            setCheckoutLoading(null);
        }
    };

    return (
        <div className="mx-auto max-w-5xl space-y-8">
            <div>
                <h2 className="text-2xl font-bold">Billing & Plans</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                    Choose the plan that fits your organisation. All prices in INR + GST.
                </p>
            </div>

            {/* Active subscription banner */}
            {activeSub && (
                <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                    <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <div className="flex-1 text-sm">
                        <span className="font-semibold">{activeSub.planName}</span> is active · {activeSub.seatCount} seats ·&nbsp;
                        Valid until {new Date(activeSub.periodEnd).toLocaleDateString("en-IN")}
                    </div>
                </div>
            )}

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                    {error}
                </div>
            )}
            {success && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                    {success}
                </div>
            )}

            {/* Billing cycle toggle + seat slider */}
            <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-1 rounded-lg border p-1">
                    {(["Monthly", "Annually"] as Cycle[]).map(c => (
                        <button
                            key={c}
                            type="button"
                            onClick={() => setCycle(c)}
                            className={cn(
                                "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                                cycle === c
                                    ? "bg-primary text-primary-foreground"
                                    : "text-foreground/60 hover:bg-accent"
                            )}
                        >
                            {c}
                            {c === "Annually" && (
                                <span className="ml-1.5 rounded-full bg-green-100 px-1.5 py-0.5 text-xs text-green-700 dark:bg-green-800 dark:text-green-300">
                                    Save 17%
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Seats:</span>
                    <input
                        type="number"
                        min={1}
                        max={999}
                        value={seatCount}
                        onChange={e => setSeatCount(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-20 rounded-md border px-2 py-1 text-center text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                </div>
            </div>

            {/* Plan cards */}
            {plansQ.isLoading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground">
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Loading plans…
                </div>
            ) : plansQ.error ? (
                <p className="text-sm text-red-600">Failed to load plans.</p>
            ) : (
                <div className="grid gap-6 md:grid-cols-3">
                    {(plansQ.data ?? []).map(plan => (
                        <PlanCard
                            key={plan.id}
                            plan={plan}
                            cycle={cycle}
                            seatCount={seatCount}
                            onSelect={() => void handleSelect(plan)}
                            isCurrentPlan={activeSub?.planId === plan.id}
                            loading={checkoutLoading === plan.id}
                        />
                    ))}
                </div>
            )}

            {/* Payment info footer */}
            <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
                <CreditCard className="h-4 w-4 shrink-0" />
                Payments are processed securely by Razorpay. We accept UPI, net banking, credit &amp; debit cards.
                <Zap className="ml-auto h-4 w-4 shrink-0 text-yellow-500" />
                Instant activation on payment.
            </div>
        </div>
    );
}
